import React, { useEffect, useState, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Table,
  Button,
  Badge,
  CardTitle,
  Alert,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import NotaDatoOriginal from "../../components/Common/NotaDatoOriginal";
import PageSpinner from "../../components/Common/PageSpinner";
import AIChatButton from "../../components/Common/AIChatButton";
import moment from "moment";
import "moment/locale/es";
import {
  getDocs,
  getDoc,
  doc,
  orderBy,
  query,
  limit,
} from "firebase/firestore";
import { getVersionedCollection } from "../../utils/firestorePaths";
import { connect } from "react-redux";
import PlateFormat from "../../components/PlateFormat";
import { formatCurrency } from "../../utils/formatCurrency";

function compareVersions(v1, v2) {
  try {
    const v1Parts = v1.split(".").map(Number);
    const v2Parts = v2.split(".").map(Number);

    for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
      const v1Part = v1Parts[i] || 0; // Si no hay más partes, se considera como 0
      const v2Part = v2Parts[i] || 0;

      if (v1Part < v2Part) {
        return true;
      }
      if (v1Part > v2Part) {
        return false;
      }
    }
  } catch (error) {
    console.error("Error al comparar versiones:", error.message);
    console.log("v1", v1);
    console.log("v2", v2);
    return false;
  }
}

// ---- Formato moneda ----
const formatCurrencyLocal = (num) => formatCurrency(num ?? 0, true);

// Un resumen es PARCIAL si se calculo con el dia todavia en curso. Se mira
// `generated_at` (hora de corte del calculo), NO el campo `temporal`: ese se
// congela en 1 y nunca vuelve a 0 en dias cerrados, asi que marcaria como
// parcial a casi todo el historico. Los documentos viejos no traen
// `generated_at`; para esos solo cuenta si es el dia de hoy.
const esParcial = (s) => {
  if (!s) return false;
  if (s.date === moment().format("YYYY-MM-DD")) return true;
  if (!s.generated_at) return false;
  const g = moment(s.generated_at, "YYYY-MM-DDTHH:mm:ss");
  return g.isValid() && g.format("YYYY-MM-DD") === s.date;
};

// ---- Componente principal ----
const ResumeDay = ({ company_id, versionDB, country, syncVersion }) => {
  const [summaries, setSummaries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchDate, setSearchDate] = useState("");
  const [notFoundMsg, setNotFoundMsg] = useState("");
  document.title = "Resumen Diario | TicketControl Monitor";

  const [versionOk, setVersionOk] = useState(true);

  useEffect(() => {
    const mayor = compareVersions("1.2.172", versionDB);
    setVersionOk(mayor);
  }, [versionDB, company_id]);

  useEffect(() => {
    const fetchSummaries = async () => {
      if (!company_id) return;
      try {
        const ref = getVersionedCollection(company_id, "resumeDay", syncVersion);
        // 🔹 Solo los 30 registros más recientes
        const q = query(ref, orderBy("date", "desc"), limit(30));
        const snapshot = await getDocs(q);
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setSummaries(data);
        const yesterday = moment().subtract(1, "day").format("YYYY-MM-DD");
        const found = data.find((d) => d.date === yesterday) || data[0];
        setSelected(found);
      } catch (err) {
        console.error("Error al cargar resúmenes:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSummaries();
  }, [company_id, syncVersion]);

  // 🔍 Buscar informe por fecha manualmente
  const handleSearchByDate = async () => {
    if (!searchDate || !company_id) return;
    setNotFoundMsg("");
    try {
      const colRef = getVersionedCollection(company_id, "resumeDay", syncVersion);
      const ref = doc(colRef, searchDate);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setSelected({ id: snap.id, ...snap.data() });
        setNotFoundMsg("");
      } else {
        setNotFoundMsg(
          "No hay informes disponibles para la fecha seleccionada."
        );
      }
    } catch (err) {
      console.error("Error al buscar informe:", err);
      setNotFoundMsg("Error al buscar el informe. Intente nuevamente.");
    }
  };

  if (!versionOk)
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Resumen Diario" breadcrumbItem="Resumen Diario" />
          <Card className="mb-3">
            <CardBody>
              <h5>
                Esta funcionalidad requiere una actualización de TicketControl
                en el Estacionamiento.
              </h5>
              <p className="text-muted">
                Esta funcionalidad requiere una versión de{" "}
                <b>TicketControl igual o superior a la 1.2.173</b>. Actualice
                TicketControl en su estacionamiento para poder utilizarla.
              </p>
            </CardBody>
          </Card>
        </Container>
      </div>
    );

  if (loading)
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Resumen Diario" breadcrumbItem="Resumen Diario" />
          <PageSpinner />
        </Container>
      </div>
    );

  if (!summaries.length)
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Resumen Diario" breadcrumbItem="Resumen Diario" />
          <h5>No hay resúmenes disponibles</h5>
          <p className="text-muted">
            Aún no se han generado informes diarios para este estacionamiento.
          </p>
        </Container>
      </div>
    );

  if (!selected)
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Resumen Diario" breadcrumbItem="Resumen Diario" />
          <h5>Seleccioná una fecha para ver el informe</h5>
        </Container>
      </div>
    );

  const summary = selected;

  // ---- Parseo de JSON ----
  const payments =
    typeof summary.payments_json === "string"
      ? JSON.parse(summary.payments_json)
      : summary.payments_json || {};

  const dealsCC =
    typeof summary.deals_cc_json === "string"
      ? JSON.parse(summary.deals_cc_json)
      : summary.deals_cc_json || [];

  const canceledTickets =
    typeof summary.tickets_canceled_json === "string"
      ? JSON.parse(summary.tickets_canceled_json)
      : summary.tickets_canceled_json || [];

  const alertsInOut =
    typeof summary.alerts_inout_json === "string"
      ? JSON.parse(summary.alerts_inout_json)
      : summary.alerts_inout_json || [];

  // Detalle de los egresos del día. El desktop lo manda desde siempre y no se
  // mostraba en ninguna pantalla: el dueño veía el Neto más bajo que los
  // Ingresos y no tenía dónde ver por qué.
  const cashNegative =
    typeof summary.cash_negative_json === "string"
      ? JSON.parse(summary.cash_negative_json)
      : summary.cash_negative_json || [];

  // Informe PARCIAL. El resumen del dia en curso es una FOTO: se publica cuando
  // alguien pide el informe y no vuelve a moverse hasta que cierra el dia. Sin
  // avisarlo, el dueno ve un informe que "no avanza" y llama a soporte.
  const horaCorte = summary.generated_at
    ? moment(summary.generated_at, "YYYY-MM-DDTHH:mm:ss")
    : null;
  const esDiaEnCurso = esParcial(summary);

  // ---- Totales ----
  const totalPayments = Object.values(payments).reduce((a, b) => a + b, 0);
  const totalDealsCC = dealsCC.reduce((a, b) => a + (b.total || 0), 0);
  const totalEgresos = cashNegative.reduce((a, b) => a + (b.value || 0), 0);

  // Residuo entre el ingreso real de caja y lo que suman los orígenes.
  const otrosIngresos =
    (summary.income_total || 0) -
    ((summary.tickets_total_amount || 0) +
      (summary.monthly_amount || 0) +
      (summary.deals_amount || 0) +
      (summary.manual_income_amount || 0));

  // Tarifa plana entra en las sumas: sin ella los tickets de ese tipo
  // desaparecian de Entradas y Salidas sin dejar ninguna inconsistencia
  // visible, que es el modo mas peligroso de fallar.
  const totalEntradas =
    (summary.tickets_hour_count_in || 0) +
    (summary.tickets_stay_count_in || 0) +
    (summary.tickets_tn_count_in || 0) +
    (summary.tickets_plana_count_in || 0);

  const totalSalidas =
    (summary.tickets_hour_count_out || 0) +
    (summary.tickets_stay_count_out || 0) +
    (summary.tickets_tn_count_out || 0) +
    (summary.tickets_plana_count_out || 0);

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Resumen Diario" breadcrumbItem="Resumen Diario" />
        <Row>
          {/* Fechas disponibles */}
          <Col md="3" className="border-end d-none d-md-block">
            <h6 className="text-muted text-uppercase mb-3" style={{ fontSize: 12, letterSpacing: 0.5 }}>
              <i className="fas fa-calendar-alt me-1"></i>Fechas disponibles
            </h6>
            <div
              style={{
                maxHeight: "calc(90vh - 60px)",
                overflowY: "auto",
                paddingRight: 10,
              }}
            >
              {summaries.map((s, i) => (
                <Button
                  key={i}
                  color={selected?.id === s.id ? "primary" : "light"}
                  className="w-100 text-start mb-2"
                  onClick={() => setSelected(s)}
                >
                  {moment(s.date).format("DD/MM/YYYY")}
                  {esParcial(s) && (
                    <span
                      // Sobre el fondo primary del item seleccionado, text-muted
                      // queda ilegible.
                      className={`ms-1 ${selected?.id === s.id ? "text-white-50" : "text-muted"}`}
                      style={{ fontSize: 11 }}
                    >
                      (parcial)
                    </span>
                  )}
                  <Badge
                    color={
                      s.net_result > 0
                        ? "success"
                        : s.net_result < 0
                        ? "danger"
                        : "secondary"
                    }
                    className="float-end"
                  >
                    {formatCurrencyLocal(s.net_result || 0)}
                  </Badge>
                </Button>
              ))}

              {/* 🔍 Buscar informes antiguos */}
              <div className="mt-4 p-2 border-top">
                <h6>Buscar informes más antiguos</h6>
                <p className="text-muted" style={{ fontSize: 12 }}>
                  Para buscar informes más antiguos, seleccioná una fecha.
                </p>
                <input
                  type="date"
                  className="form-control mb-2"
                  value={searchDate}
                  onChange={(e) => setSearchDate(e.target.value)}
                />
                <Button
                  color="secondary"
                  size="sm"
                  className="w-100"
                  onClick={handleSearchByDate}
                >
                  Buscar informe
                </Button>

                {notFoundMsg && (
                  <div className="mt-2 text-danger small">{notFoundMsg}</div>
                )}
              </div>
            </div>
          </Col>

          {/* Selector móvil */}
          <Col xs="12" className="d-md-none mb-3">
            <Card className="shadow-sm mb-0">
              <CardBody className="py-2 px-3">
                <label htmlFor="mobileDateSelect" className="text-muted mb-1 d-block" style={{ fontSize: 12 }}>
                  <i className="fas fa-calendar-alt me-1"></i>Seleccioná una fecha
                </label>
                <select
                  id="mobileDateSelect"
                  className="form-select form-select-sm"
                  value={selected?.id || ""}
                  onChange={(e) => {
                    const found = summaries.find((s) => s.id === e.target.value);
                    if (found) setSelected(found);
                  }}
                >
                  {summaries.map((s, i) => (
                    <option key={i} value={s.id}>
                      {moment(s.date).format("DD/MM/YYYY")}
                      {esParcial(s) ? " (parcial)" : ""} — {formatCurrencyLocal(s.net_result || 0)}
                    </option>
                  ))}
                </select>
              </CardBody>
            </Card>
          </Col>

          {/* 📊 Detalle principal */}
          <Col md="9">
            <Card className="shadow-sm">
              <CardHeader className="bg-primary bg-gradient text-white py-3">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h5 className="mb-0 text-white">
                      <i className="fas fa-cash-register me-2"></i>
                      Caja del día {moment(summary.date).format("DD/MM/YYYY")}
                    </h5>
                    {esDiaEnCurso && (
                      <div className="text-white-50 mt-1" style={{ fontSize: 12 }}>
                        <i className="fas fa-hourglass-half me-1"></i>
                        <strong>Informe parcial</strong>
                        {horaCorte && horaCorte.isValid()
                          ? ` — datos hasta las ${horaCorte.format("HH:mm")}, se actualiza cada hora`
                          : " — el día todavía no cerró"}
                      </div>
                    )}
                  </div>
                  <Badge color={summary.net_result >= 0 ? "success" : "danger"} className="px-2 py-1 flex-shrink-0 ms-2" style={{ fontSize: 13 }}>
                    {formatCurrencyLocal(summary.net_result || 0)}
                  </Badge>
                </div>
              </CardHeader>

              <CardBody>
                {/* Explicacion completa de por que el informe no avanza. El
                    resumen diario se genera al cerrar el dia; el del dia en
                    curso es una foto del momento en que se pidio. Decirlo aca
                    evita la llamada a soporte. */}
                {esDiaEnCurso && (
                  <Alert color="info" className="d-flex align-items-start py-2 mb-3">
                    <i className="fas fa-circle-info me-2 mt-1"></i>
                    <div style={{ fontSize: 13 }}>
                      <strong>Este informe es parcial: el día todavía no cerró.</strong>{" "}
                      {horaCorte && horaCorte.isValid()
                        ? `Incluye la actividad hasta las ${horaCorte.format("HH:mm")}.`
                        : ""}
                      <br />
                      Se actualiza <strong>aproximadamente cada hora</strong>, así
                      que si acabás de cobrar algo puede que todavía no figure.
                      Pasada la medianoche vas a ver acá el informe completo y
                      definitivo del día.
                      <br />
                      Para ver cómo va el día <strong>en este momento</strong>,
                      entrá a <strong>Estadísticas</strong>: ahí los datos se
                      actualizan en vivo.
                    </div>
                  </Alert>
                )}

                {/* === TARJETAS RESUMEN === */}
                <Row className="mb-4 text-center g-2 g-md-3">
                  <Col md="4" xs="4">
                    <Card className="shadow-sm mb-0 h-100" style={{ borderLeft: "3px solid var(--bs-success)" }}>
                      <CardBody className="py-2 px-3">
                        <div className="text-muted mb-1" style={{ fontSize: 12 }}>
                          <i className="fas fa-arrow-up me-1 text-success"></i>Ingresos
                        </div>
                        <h5 className="text-success mb-0">
                          {formatCurrencyLocal(summary.income_total || 0)}
                        </h5>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="4" xs="4">
                    <Card className="shadow-sm mb-0 h-100" style={{ borderLeft: "3px solid var(--bs-danger)" }}>
                      <CardBody className="py-2 px-3">
                        <div className="text-muted mb-1" style={{ fontSize: 12 }}>
                          <i className="fas fa-arrow-down me-1 text-danger"></i>Egresos
                        </div>
                        <h5 className="text-danger mb-0">
                          {formatCurrencyLocal(summary.expense_total || 0)}
                        </h5>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md="4" xs="4">
                    <Card className="shadow-sm mb-0 h-100" style={{ borderLeft: `3px solid var(--bs-${summary.net_result >= 0 ? "success" : "danger"})` }}>
                      <CardBody className="py-2 px-3">
                        <div className="text-muted mb-1" style={{ fontSize: 12 }}>
                          <i className="fas fa-calculator me-1"></i>Neto
                        </div>
                        <h5
                          className={`mb-0 ${
                            summary.net_result >= 0
                              ? "text-success"
                              : "text-danger"
                          }`}
                        >
                          {formatCurrencyLocal(summary.net_result || 0)}
                        </h5>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* === INGRESOS POR MEDIOS DE PAGO === */}
                <h6 className="mb-2 text-muted text-uppercase" style={{ fontSize: 13, letterSpacing: 0.5 }}>
                  <i className="fas fa-credit-card me-1"></i>Ingresos por Medios de Pago
                </h6>
                <Table bordered size="sm" responsive className="mb-4">
                  <tbody>
                    {Object.entries(payments).map(([method, value], i) => (
                      <tr key={i}>
                        <td>{method}</td>
                        <td className="text-end">{formatCurrencyLocal(value)}</td>
                      </tr>
                    ))}
                    <tr className="fw-bold table-light">
                      <td>Total</td>
                      <td className="text-end">
                        {formatCurrencyLocal(totalPayments)}
                      </td>
                    </tr>
                  </tbody>
                </Table>

                {/* === INGRESOS POR ORIGEN === */}
                {/* <div>{JSON.stringify(summary || {}, null, 2)}</div> */}

                <h6 className="mb-2 text-muted text-uppercase" style={{ fontSize: 13, letterSpacing: 0.5 }}>
                  <i className="fas fa-layer-group me-1"></i>Ingresos por Origen
                </h6>
                <Table bordered size="sm" responsive className="mb-4">
                  <thead>
                    <tr>
                      <th>Tipo</th>
                      <th className="text-end">Cantidad</th>
                      <th className="text-end">Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(summary.tickets_hour_count_out > 0 ||
                      summary.tickets_hour_amount > 0) && (
                      <tr>
                        <td>Tickets por Hora</td>
                        <td className="text-end">
                          {summary.tickets_hour_count_out || 0}
                        </td>
                        <td className="text-end">
                          {formatCurrencyLocal(summary.tickets_hour_amount || 0)}
                        </td>
                      </tr>
                    )}
                    {(summary.hasStay ||
                      summary.tickets_stay_count_out > 0 ||
                      summary.tickets_stay_amount > 0) && (
                      <tr>
                        <td>Tickets por Estadía</td>
                        <td className="text-end">
                          {summary.tickets_stay_count_out || 0}
                        </td>
                        <td className="text-end">
                          {formatCurrencyLocal(summary.tickets_stay_amount || 0)}
                        </td>
                      </tr>
                    )}
                    {(summary.hasTurn ||
                      summary.tickets_tn_count_out > 0 ||
                      summary.tickets_tn_amount > 0) && (
                      <tr>
                        <td>Tickets por Turno</td>
                        <td className="text-end">
                          {summary.tickets_tn_count_out || 0}
                        </td>
                        <td className="text-end">
                          {formatCurrencyLocal(summary.tickets_tn_amount || 0)}
                        </td>
                      </tr>
                    )}
                    {(summary.hasPlana ||
                      summary.tickets_plana_count_out > 0 ||
                      summary.tickets_plana_amount > 0) && (
                      <tr>
                        <td>Tickets por Tarifa Plana</td>
                        <td className="text-end">
                          {summary.tickets_plana_count_out || 0}
                        </td>
                        <td className="text-end">
                          {formatCurrencyLocal(summary.tickets_plana_amount || 0)}
                        </td>
                      </tr>
                    )}

                    <tr className="table-light fw-bold">
                      <td>Subtotal Tickets</td>
                      <td className="text-end">
                        {summary.tickets_total_count_out || 0}
                      </td>
                      <td className="text-end">
                        {formatCurrencyLocal(summary.tickets_total_amount || 0)}
                      </td>
                    </tr>

                    {/* `?? true` a proposito: un documento viejo no trae el
                        flag y la fila tiene que seguir apareciendo como hasta
                        ahora. Mismo default que el informe del desktop. */}
                    {((summary.hasCC ?? true) ||
                      summary.monthly_count > 0 ||
                      summary.monthly_amount > 0) && (
                    <tr>
                      <td>Mensuales</td>
                      <td className="text-end">{summary.monthly_count || 0}</td>
                      <td className="text-end">
                        {formatCurrencyLocal(summary.monthly_amount || 0)}
                      </td>
                    </tr>
                    )}

                    <tr>
                      <td>Acuerdos</td>
                      <td className="text-end">{summary.deals_count || 0}</td>
                      <td className="text-end">
                        {formatCurrencyLocal(summary.deals_amount || 0)}
                      </td>
                    </tr>

                    <tr>
                      <td>Ingresos manuales</td>
                      <td className="text-end">
                        {summary.manual_income_count || 0}
                      </td>
                      <td className="text-end">
                        {formatCurrencyLocal(summary.manual_income_amount || 0)}
                      </td>
                    </tr>

                    {/* Cualquier cobro que no caiga en ninguna categoría se
                        evaporaba del desglose sin dejar rastro, y el Total de
                        esta tabla no coincidía con la tarjeta "Ingresos".
                        Misma fórmula y misma tolerancia de $1 que el informe
                        del desktop, para que las dos vistas digan lo mismo. */}
                    {Math.abs(otrosIngresos) >= 1 && (
                      <tr>
                        <td>
                          Otros <span className="text-muted">(sin categoría)</span>
                        </td>
                        <td className="text-end">—</td>
                        <td className="text-end">
                          {formatCurrencyLocal(otrosIngresos)}
                        </td>
                      </tr>
                    )}

                    <tr className="fw-bold table-light">
                      <td>Total</td>
                      <td className="text-end">
                        {(summary.tickets_total_count_out || 0) +
                          (summary.monthly_count || 0) +
                          (summary.deals_count || 0) +
                          (summary.manual_income_count || 0)}
                      </td>
                      <td className="text-end">
                        {formatCurrencyLocal(summary.income_total || 0)}
                      </td>
                    </tr>
                  </tbody>
                </Table>

                {/* === EGRESOS DEL DÍA ===
                    Es el desglose exacto del número que muestra la tarjeta
                    "Egresos" de arriba: misma query, mismo rango, mismo
                    criterio (value<0 y type!='init'). Si esa tarjeta muestra
                    algo distinto de cero, esta tabla no puede estar vacía.
                    Sin esto el dueño veía el Neto más bajo que los Ingresos y
                    no tenía en toda la pantalla dónde ver por qué. */}
                {cashNegative.length > 0 && (
                  <>
                    <h6 className="mb-1 text-muted text-uppercase" style={{ fontSize: 13, letterSpacing: 0.5 }}>
                      <i className="fas fa-arrow-trend-down me-1 text-danger"></i>
                      Egresos del día ({cashNegative.length}{" "}
                      {cashNegative.length === 1 ? "movimiento" : "movimientos"})
                    </h6>
                    <p className="text-muted small mb-2">
                      Salidas de dinero de la caja: devoluciones, anulaciones y
                      retiros. Explican la diferencia entre los ingresos y el
                      resultado neto. No incluye el fondo de caja.
                    </p>

                    {/* Desktop: tabla */}
                    <Table bordered size="sm" responsive className="mb-2 d-none d-md-table">
                      <thead>
                        <tr>
                          <th>Hora</th>
                          <th>Descripción</th>
                          <th>Operador</th>
                          <th className="text-end">Monto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cashNegative.map((m, i) => (
                          <tr key={i}>
                            <td className="text-nowrap">
                              {moment(m.date).format("DD/MM HH:mm")}
                            </td>
                            <td>{m.description}</td>
                            <td>{m.cashier}</td>
                            <td className="text-end text-danger fw-semibold">
                              {formatCurrencyLocal(m.value)}
                            </td>
                          </tr>
                        ))}
                        <tr className="fw-bold table-light">
                          <td colSpan={3}>Total</td>
                          <td className="text-end text-danger">
                            {formatCurrencyLocal(totalEgresos)}
                          </td>
                        </tr>
                      </tbody>
                    </Table>

                    {/* Mobile: cards */}
                    <div className="d-md-none mb-2">
                      {cashNegative.map((m, i) => (
                        <Card key={i} className="mb-2 border" style={{ borderLeft: "3px solid var(--bs-danger)" }}>
                          <CardBody className="py-2 px-3">
                            <div className="d-flex justify-content-between align-items-start">
                              <span style={{ fontSize: 13 }}>{m.description}</span>
                              <span className="text-danger fw-semibold ms-2 text-nowrap" style={{ fontSize: 14 }}>
                                {formatCurrencyLocal(m.value)}
                              </span>
                            </div>
                            <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                              <i className="fas fa-clock me-1"></i>
                              {moment(m.date).format("DD/MM HH:mm")}
                              <span className="ms-2">
                                <i className="fas fa-user me-1"></i>
                                {m.cashier}
                              </span>
                            </div>
                          </CardBody>
                        </Card>
                      ))}
                      <div className="d-flex justify-content-between fw-bold px-1">
                        <span>Total</span>
                        <span className="text-danger">
                          {formatCurrencyLocal(totalEgresos)}
                        </span>
                      </div>
                    </div>
                  </>
                )}

                {/* === CARGOS A CUENTAS CORRIENTES DE EMPRESAS ===
                    Decía "Ingresos Futuros por Acuerdos", que es engañoso: son
                    CARGOS a la cuenta de la empresa, y no hay ninguna garantía
                    de que se cobren. Mismo texto que el informe del desktop,
                    para que el dueño no vea dos cosas distintas del mismo dato. */}
                {dealsCC.length > 0 && (
                  <>
                    <h6 className="mb-1 text-muted text-uppercase" style={{ fontSize: 13, letterSpacing: 0.5 }}>
                      <i className="fas fa-handshake me-1"></i>Cargado a Cuentas Corrientes de Empresas
                    </h6>
                    <p className="text-muted small mb-2">
                      Consumo de las empresas con convenio que se les cargó a su
                      cuenta corriente <strong>este día</strong>. No es el saldo que adeuda
                      cada empresa.
                      <br />
                      No son movimientos de caja, por eso no suman al resultado
                      neto, y este listado no indica si ya fueron pagados. Los
                      pagos que hacen las empresas sí entran a la caja y
                      aparecen en «Acuerdos», dentro de Ingresos por Origen.
                    </p>
                    <Table bordered size="sm" responsive className="mb-2">
                      <thead>
                        <tr>
                          <th>Empresa</th>
                          <th>Acuerdo</th>
                          <th className="text-end">Movimientos</th>
                          <th className="text-end">Monto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dealsCC.map((r, i) => (
                          <tr key={i}>
                            <td>{r.company}</td>
                            <td>{r.deal_name}</td>
                            <td className="text-end">{r.cnt}</td>
                            <td className="text-end">
                              {formatCurrencyLocal(r.total)}
                            </td>
                          </tr>
                        ))}
                        <tr className="fw-bold table-light">
                          <td colSpan={2}>Total</td>
                          <td className="text-end">
                            {summary.deals_cc_count || 0}
                          </td>
                          <td className="text-end">
                            {formatCurrencyLocal(totalDealsCC)}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </>
                )}
              </CardBody>
            </Card>

            <Card className="shadow-sm mt-4">
              <CardHeader className="bg-primary bg-gradient text-white py-3">
                <div className="d-flex align-items-center justify-content-between">
                  <h5 className="mb-0 text-white">
                    <i className="fas fa-ticket-alt me-2"></i>
                    Tickets del día {moment(summary.date).format("DD/MM/YYYY")}
                  </h5>
                  <div className="text-end flex-shrink-0 d-flex flex-column gap-1">
                    <Badge color="success" style={{ fontSize: 11 }}>
                      <i className="fas fa-arrow-right me-1"></i>{totalEntradas}
                    </Badge>
                    <Badge color="danger" style={{ fontSize: 11 }}>
                      <i className="fas fa-arrow-left me-1"></i>{totalSalidas}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                {/* === TICKETS === */}
                {/* <h4 className="mt-0 mb-4">Tickets</h4> */}

                {/* ENTRADAS */}
                <Row>
                  <Col md="6">
                    <h6 className="mb-2 text-muted text-uppercase" style={{ fontSize: 13, letterSpacing: 0.5 }}>
                      <i className="fas fa-arrow-right me-1 text-success"></i>Entradas
                    </h6>
                    <Table bordered size="sm" responsive className="mb-4">
                      <thead>
                        <tr>
                          <th>Tipo</th>
                          <th className="text-end">Cantidad</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.tickets_hour_count_in > 0 && (
                          <tr>
                            <td>Por Hora</td>
                            <td className="text-end">
                              {summary.tickets_hour_count_in || 0}
                            </td>
                          </tr>
                        )}
                        {(summary.hasStay || summary.tickets_stay_count_in > 0) && (
                          <tr>
                            <td>Por Estadía</td>
                            <td className="text-end">
                              {summary.tickets_stay_count_in || 0}
                            </td>
                          </tr>
                        )}
                        {(summary.hasTurn || summary.tickets_tn_count_in > 0) && (
                          <tr>
                            <td>Por Turno</td>
                            <td className="text-end">
                              {summary.tickets_tn_count_in || 0}
                            </td>
                          </tr>
                        )}
                        {(summary.hasPlana || summary.tickets_plana_count_in > 0) && (
                          <tr>
                            <td>Tarifa Plana</td>
                            <td className="text-end">
                              {summary.tickets_plana_count_in || 0}
                            </td>
                          </tr>
                        )}
                        <tr className="fw-bold table-light">
                          <td>Total</td>
                          <td className="text-end">{totalEntradas}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                  <Col md="6">
                    {/* SALIDAS */}
                    <h6 className="mb-2 text-muted text-uppercase" style={{ fontSize: 13, letterSpacing: 0.5 }}>
                      <i className="fas fa-arrow-left me-1 text-danger"></i>Salidas
                    </h6>
                    <Table bordered size="sm" responsive className="mb-4">
                      <thead>
                        <tr>
                          <th>Tipo</th>
                          <th className="text-end">Cantidad</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.tickets_hour_count_out > 0 && (
                          <tr>
                            <td>Por Hora</td>
                            <td className="text-end">
                              {summary.tickets_hour_count_out || 0}
                            </td>
                          </tr>
                        )}
                        {(summary.hasStay || summary.tickets_stay_count_out > 0) && (
                          <tr>
                            <td>Por Estadía</td>
                            <td className="text-end">
                              {summary.tickets_stay_count_out || 0}
                            </td>
                          </tr>
                        )}
                        {(summary.hasTurn || summary.tickets_tn_count_out > 0) && (
                          <tr>
                            <td>Por Turno</td>
                            <td className="text-end">
                              {summary.tickets_tn_count_out || 0}
                            </td>
                          </tr>
                        )}
                        {(summary.hasPlana || summary.tickets_plana_count_out > 0) && (
                          <tr>
                            <td>Tarifa Plana</td>
                            <td className="text-end">
                              {summary.tickets_plana_count_out || 0}
                            </td>
                          </tr>
                        )}
                        <tr className="fw-bold table-light">
                          <td>Total</td>
                          <td className="text-end">{totalSalidas}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                </Row>

                {/* CANCELADOS */}
                {canceledTickets.length > 0 && (
                  <>
                    <h6 className="mb-2 text-muted text-uppercase" style={{ fontSize: 13, letterSpacing: 0.5 }}>
                      <i className="fas fa-ban me-1 text-warning"></i>Cancelados ({canceledTickets.length} tickets)
                    </h6>

                    {/* Desktop: tabla */}
                    <Table bordered size="sm" responsive className="d-none d-md-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Matrícula</th>
                          <th>Ingreso</th>
                          <th>Egreso</th>
                          <th>Permanencia</th>
                          <th>Cancelado por</th>
                          <th>Motivo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {canceledTickets.map((t, i) => (
                          <tr key={i}>
                            <td>{t.ticket_id}</td>
                            <td><b>{PlateFormat(t.plate, country)}</b></td>
                            <td>{moment(t.init_date).format("DD/MM/YY HH:mm")}</td>
                            <td>{moment(t.end_date).format("DD/MM/YY HH:mm")}</td>
                            <td>{t.permanence}</td>
                            <td>{t.full_name || t.user}</td>
                            <td>{t.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    {/* Mobile: cards */}
                    <div className="d-md-none">
                      {canceledTickets.map((t, i) => (
                        <Card key={i} className="mb-2 border" style={{ borderLeft: "3px solid var(--bs-warning)" }}>
                          <CardBody className="py-2 px-3">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <span style={{ fontSize: 15, fontWeight: 600 }}>{PlateFormat(t.plate, country)}</span>
                                <span className="text-muted ms-2" style={{ fontSize: 12 }}>ID {t.ticket_id}</span>
                              </div>
                              <Badge color="warning" className="text-dark" style={{ fontSize: 10 }}>CANCELADO</Badge>
                            </div>
                            <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                              <i className="fas fa-arrow-right me-1 text-success"></i>
                              {moment(t.init_date).format("DD/MM HH:mm")}
                              <i className="fas fa-arrow-left ms-2 me-1 text-danger"></i>
                              {moment(t.end_date).format("DD/MM HH:mm")}
                              {t.permanence && <span className="ms-2"><i className="fas fa-clock me-1"></i>{t.permanence}</span>}
                            </div>
                            <div className="mt-1" style={{ fontSize: 12 }}>
                              <span className="text-muted">Por: </span>{t.full_name || t.user}
                              {t.reason && <span className="ms-2 text-muted">— {t.reason}</span>}
                            </div>
                          </CardBody>
                        </Card>
                      ))}
                    </div>
                  </>
                )}
              </CardBody>
            </Card>

            {/* === ALERTAS === */}
            {alertsInOut.length > 0 && (
              <Card className="shadow-sm mt-4">
                <CardHeader className="bg-warning bg-gradient text-dark py-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <h5 className="mb-0">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      Alertas del día {moment(summary.date).format("DD/MM/YYYY")}
                    </h5>
                    <Badge color="dark" style={{ fontSize: 11 }}>
                      {alertsInOut.length}
                    </Badge>
                  </div>
                </CardHeader>
                <CardBody>
                  {/* Desktop: tabla */}
                  <Table bordered size="sm" responsive className="mb-0 d-none d-md-table">
                    <thead>
                      <tr>
                        <th>Hora</th>
                        <th>Alerta</th>
                        <th>Matrícula</th>
                        <th>Detalle</th>
                        <th>Operador</th>
                      </tr>
                    </thead>
                    <tbody>
                      {alertsInOut.map((a, i) => (
                        <tr key={i}>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {moment(a.date).format("HH:mm")}
                          </td>
                          <td>
                            <Badge color={
                              a.alert?.includes("manual") ? "warning" :
                              a.alert?.includes("excedido") ? "info" :
                              a.alert?.includes("pago") ? "danger" :
                              a.alert?.includes("negra") ? "dark" :
                              "secondary"
                            } className={a.alert?.includes("manual") ? "text-dark" : ""} style={{ fontSize: 11 }}>
                              {a.alert}
                            </Badge>
                          </td>
                          <td><b>{PlateFormat(a.plate, country)}</b></td>
                          <td><small className="text-muted">{a.detail}</small></td>
                          <td>{a.cashier || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {/* Mobile: cards */}
                  <div className="d-md-none">
                    {alertsInOut.map((a, i) => {
                      const badgeColor =
                        a.alert?.includes("manual") ? "warning" :
                        a.alert?.includes("excedido") ? "info" :
                        a.alert?.includes("pago") ? "danger" :
                        a.alert?.includes("negra") ? "dark" :
                        "secondary";
                      return (
                        <Card key={i} className="mb-2 border" style={{ borderLeft: `3px solid var(--bs-${badgeColor})` }}>
                          <CardBody className="py-2 px-3">
                            <div className="d-flex justify-content-between align-items-start">
                              <span style={{ fontSize: 15, fontWeight: 600 }}>{a.plate ? PlateFormat(a.plate, country) : "—"}</span>
                              <span className="text-muted" style={{ fontSize: 12 }}>{moment(a.date).format("HH:mm")}</span>
                            </div>
                            <div className="mt-1">
                              <Badge color={badgeColor} className={badgeColor === "warning" ? "text-dark" : ""} style={{ fontSize: 11 }}>
                                {a.alert}
                              </Badge>
                            </div>
                            {a.detail && (
                              <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                                {a.detail}
                              </div>
                            )}
                            {a.cashier && (
                              <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                                <i className="fas fa-user me-1"></i>{a.cashier}
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      );
                    })}
                  </div>
                </CardBody>
              </Card>
            )}
          </Col>
        </Row>

        {/* AI Chat */}
        <ResumeDayAI selected={selected} summaries={summaries} />
        <NotaDatoOriginal />
      </Container>
    </div>
  );
};

// ---- AI Data Summary builder ----
const fmt = (v) => formatCurrency(v ?? 0, true);

const ResumeDayAI = ({ selected, summaries }) => {
  const aiDataSummary = useMemo(() => {
    if (!selected) return "";
    const s = selected;

    let out = `RESUMEN DEL DIA ${moment(s.date).format("DD/MM/YYYY")}\n\n`;

    // Financials
    out += `RESULTADO FINANCIERO:\n`;
    out += `- Ingresos: ${fmt(s.income_total)}\n`;
    out += `- Egresos: ${fmt(s.expense_total)}\n`;
    out += `- Neto: ${fmt(s.net_result)}\n\n`;

    // Payments by method
    const payments = typeof s.payments_json === "string" ? JSON.parse(s.payments_json) : s.payments_json || {};
    if (Object.keys(payments).length) {
      out += `INGRESOS POR FORMA DE PAGO:\n`;
      Object.entries(payments).forEach(([k, v]) => { out += `- ${k}: ${fmt(v)}\n`; });
      out += "\n";
    }

    // Income by source
    out += `INGRESOS POR ORIGEN:\n`;
    if (s.tickets_hour_count_out || s.tickets_hour_amount) out += `- Tickets por hora: ${s.tickets_hour_count_out} tickets, ${fmt(s.tickets_hour_amount)}\n`;
    if (s.tickets_stay_count_out || s.tickets_stay_amount) out += `- Tickets por estadia: ${s.tickets_stay_count_out} tickets, ${fmt(s.tickets_stay_amount)}\n`;
    if (s.tickets_tn_count_out || s.tickets_tn_amount) out += `- Tickets por turno: ${s.tickets_tn_count_out} tickets, ${fmt(s.tickets_tn_amount)}\n`;
    if (s.tickets_plana_count_out || s.tickets_plana_amount) out += `- Tickets por tarifa plana: ${s.tickets_plana_count_out} tickets, ${fmt(s.tickets_plana_amount)}\n`;
    out += `- Total tickets: ${s.tickets_total_count_out || 0} tickets, ${fmt(s.tickets_total_amount)}\n`;
    if (s.monthly_count) out += `- Mensuales: ${s.monthly_count}, ${fmt(s.monthly_amount)}\n`;
    if (s.deals_count) out += `- Acuerdos: ${s.deals_count}, ${fmt(s.deals_amount)}\n`;
    if (s.manual_income_count) out += `- Ingresos manuales: ${s.manual_income_count}, ${fmt(s.manual_income_amount)}\n`;
    const otros =
      (s.income_total || 0) -
      ((s.tickets_total_amount || 0) + (s.monthly_amount || 0) + (s.deals_amount || 0) + (s.manual_income_amount || 0));
    if (Math.abs(otros) >= 1) out += `- Otros (sin categoria): ${fmt(otros)}\n`;
    out += `- TOTAL: ${fmt(s.income_total)}\n`;
    out += "\n";

    // Egresos del dia. Sin esto, ante "por que el neto es mas bajo?" el
    // asistente no tenia ningun dato con que contestar.
    const negativos = typeof s.cash_negative_json === "string" ? JSON.parse(s.cash_negative_json) : s.cash_negative_json || [];
    if (negativos.length) {
      out += `EGRESOS DEL DIA (${negativos.length} movimientos, total ${fmt(s.expense_total)}):\n`;
      negativos.forEach((m) => {
        out += `- ${m.description || "-"} | ${fmt(m.value)} | Operador: ${m.cashier || "-"}\n`;
      });
      out += "\n";
    }

    // Entries/Exits
    const entradas = (s.tickets_hour_count_in || 0) + (s.tickets_stay_count_in || 0) + (s.tickets_tn_count_in || 0) + (s.tickets_plana_count_in || 0);
    const salidas = (s.tickets_hour_count_out || 0) + (s.tickets_stay_count_out || 0) + (s.tickets_tn_count_out || 0) + (s.tickets_plana_count_out || 0);
    out += `MOVIMIENTO DE VEHICULOS:\n`;
    out += `- Entradas: ${entradas}\n`;
    out += `- Salidas: ${salidas}\n\n`;

    // Canceled tickets
    const canceled = typeof s.tickets_canceled_json === "string" ? JSON.parse(s.tickets_canceled_json) : s.tickets_canceled_json || [];
    if (canceled.length) {
      out += `TICKETS CANCELADOS (${canceled.length}):\n`;
      canceled.forEach((t) => {
        out += `- #${t.ticket_id} | ${t.plate || "-"} | Permanencia: ${t.permanence || "-"} | Por: ${t.full_name || t.user || "-"} | Motivo: ${t.reason || "-"}\n`;
      });
      out += "\n";
    }

    // Alerts
    const alerts = typeof s.alerts_inout_json === "string" ? JSON.parse(s.alerts_inout_json) : s.alerts_inout_json || [];
    if (alerts.length) {
      out += `ALERTAS (${alerts.length}):\n`;
      const byType = {};
      alerts.forEach((a) => {
        const type = a.alert || "Otra";
        byType[type] = (byType[type] || 0) + 1;
      });
      Object.entries(byType).forEach(([k, v]) => { out += `- ${k}: ${v}\n`; });
      out += "\n";
    }

    // Cargos a cuentas corrientes de empresas (NO son ingresos ni movimientos
    // de caja: son deuda cargada a la empresa, que puede o no cobrarse).
    const dealsCC = typeof s.deals_cc_json === "string" ? JSON.parse(s.deals_cc_json) : s.deals_cc_json || [];
    if (dealsCC.length) {
      const totalDeals = dealsCC.reduce((a, b) => a + (b.total || 0), 0);
      out += `CARGADO A CUENTAS CORRIENTES DE EMPRESAS (no son movimientos de caja ni indican si ya se cobraron): ${fmt(totalDeals)}\n`;
      dealsCC.forEach((d) => { out += `- ${d.company} (${d.deal_name}): ${d.cnt} mov., ${fmt(d.total)}\n`; });
      out += "\n";
    }

    // Historical context (last 30 days)
    if (summaries.length > 1) {
      out += `CONTEXTO HISTORICO (ultimos ${summaries.length} dias):\n`;
      const incomes = summaries.map((d) => d.income_total || 0);
      const avg = incomes.reduce((a, b) => a + b, 0) / incomes.length;
      const max = Math.max(...incomes);
      const min = Math.min(...incomes);
      const bestDay = summaries.find((d) => (d.income_total || 0) === max);
      const worstDay = summaries.find((d) => (d.income_total || 0) === min);
      out += `- Promedio diario: ${fmt(avg)}\n`;
      out += `- Mejor dia: ${moment(bestDay?.date).format("DD/MM")} con ${fmt(max)}\n`;
      out += `- Peor dia: ${moment(worstDay?.date).format("DD/MM")} con ${fmt(min)}\n`;
      const todayIncome = s.income_total || 0;
      const vsProm = avg > 0 ? (((todayIncome - avg) / avg) * 100).toFixed(1) : 0;
      out += `- Este dia vs promedio: ${vsProm}%\n`;
    }

    return out;
  }, [selected, summaries]);

  const aiSuggestions = useMemo(() => [
    "¿Como estuvo el dia?",
    "¿De donde vinieron los ingresos?",
    "¿Cuantos tickets se cancelaron y por que?",
    "¿Como se compara con el promedio?",
    "¿Hubo alertas importantes?",
    "¿Que forma de pago se uso mas?",
  ], []);

  if (!aiDataSummary) return null;
  return (
    <AIChatButton
      context="resumen_diario"
      dataSummary={aiDataSummary}
      suggestions={aiSuggestions}
      title="Asistente Resumen Diario"
    />
  );
};

const mapStateToProps = (state) => ({
  company_id: state.companies.currentCompany?.id,
  country: state.companies.currentCompany?.country,
  versionDB: state.data.appConfig.version_app || "1.0.0",
  syncVersion: state.data.syncVersion,
});

export default connect(mapStateToProps)(ResumeDay);
